import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PatientList from './PatientList';
import PatientModal from './PatientModal';
import AppointmentModal from './AppointmentModal';
import Toast from './Toast';
import DoctorList from './DoctorList';
import DoctorForm from './DoctorForm';
import AppointmentList from './AppointmentList';

import CalendarView from './CalendarView';
import { clearSuccessMessage } from '../redux/slices/patientSlice';
import { setLastActiveDashboardSection } from '../redux/slices/uiSlice';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const authState = useSelector((state) => state.auth);
  const patientSuccessMessage = useSelector((state) => state.patients.successMessage);
  const lastActiveDashboardSection = useSelector((state) => state.ui.lastActiveDashboardSection);
  const [activeSection, setActiveSection] = useState(location.state?.activeSection || lastActiveDashboardSection || 'calendar');
  // Close sidebar by default on mobile, open on desktop
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedAppointmentForEdit, setSelectedAppointmentForEdit] = useState(null);
  const sidebarRef = useRef(null);
  const menuToggleRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close sidebar on pagination or form button clicks
      if (event.target.closest('.pagination-controls') || 
          event.target.closest('.pagination-btn') ||
          event.target.closest('.table-container')) {
        return;
      }

      // Check if click is outside sidebar and not on the toggle button
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuToggleRef.current &&
        !menuToggleRef.current.contains(event.target)
      ) {
        setIsSidebarOpen(false);
      }
    };

    // Only add listener if sidebar is open
    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isSidebarOpen]);

  // Handle window resize to adjust sidebar visibility
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        // Desktop: keep sidebar open
        setIsSidebarOpen(true);
      } else {
        // Mobile: close sidebar
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save active section to Redux whenever it changes
  useEffect(() => {
    console.log("Saving activeSection to Redux:", activeSection);
    dispatch(setLastActiveDashboardSection(activeSection));
  }, [activeSection, dispatch]);

  // Restore active section from Redux when navigating back to Dashboard
  useEffect(() => {
    console.log("Dashboard mounted/updated. location.state:", location.state);
    console.log("lastActiveDashboardSection from Redux:", lastActiveDashboardSection);
    
    if (!location.state?.activeSection) {
      // No activeSection in location state, restore from Redux/localStorage
      console.log("Restoring from Redux:", lastActiveDashboardSection || 'calendar');
      setActiveSection(lastActiveDashboardSection || 'calendar');
    }
  }, [location, lastActiveDashboardSection]);

  const handleLogout = () => {
    localStorage.removeItem('doctorToken');
    localStorage.removeItem('doctorId');
    window.location.href = '/login';
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'patients':
        return (
          <div className="section">
            <div className="section-header">
              <h2>Patient Management</h2>
              <button 
                className="create-btn"
                onClick={() => setIsPatientModalOpen(true)}
              >
                + Add Patient
              </button>
            </div>
            <PatientList />
          </div>
        );
      case 'doctors':
        return (
          <div className="section">
            <h2>Doctor Management</h2>
            <DoctorForm />
            <DoctorList />
          </div>
        );
      case 'appointments':
        return (
          <div className="section">
            <div className="section-header">
              <h2>Appointment Management</h2>
              <button 
                className="create-btn schedule-appointment-btn"
                onClick={() => {
                  setSelectedAppointmentForEdit(null);
                  setIsAppointmentModalOpen(true);
                }}
              >
                Schedule Appointment
              </button>
            </div>
            <AppointmentList onEdit={(appointment) => {
              setSelectedAppointmentForEdit(appointment);
              setIsAppointmentModalOpen(true);
            }} currentSection={activeSection} />
          </div>
        );
      case 'calendar':
        return (
          <div className="section">
            <CalendarView currentSection={activeSection} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard">
      <Toast 
        message={patientSuccessMessage} 
        type="success" 
        onClose={() => dispatch(clearSuccessMessage())}
      />
      <PatientModal 
        isOpen={isPatientModalOpen} 
        onClose={() => setIsPatientModalOpen(false)}
      />
      <AppointmentModal 
        isOpen={isAppointmentModalOpen} 
        onClose={() => {
          setIsAppointmentModalOpen(false);
          setSelectedAppointmentForEdit(null);
        }}
        appointment={selectedAppointmentForEdit}
      />
      <div ref={sidebarRef} className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h1 className="sidebar-title">Doctaura.</h1>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeSection === 'calendar' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('calendar');
              setIsSidebarOpen(false);
            }}
          >
            <span className="nav-icon">📆</span>
            <span className="nav-text">Calendar View</span>
          </button>
          
          <button
            className={`nav-item ${activeSection === 'patients' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('patients');
              setIsSidebarOpen(false);
            }}
          >
            <span className="nav-icon">👥</span>
            <span className="nav-text">Patient Management</span>
          </button>

          <button
            className={`nav-item ${activeSection === 'appointments' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('appointments');
              setIsSidebarOpen(false);
            }}
          >
            <span className="nav-icon">📅</span>
            <span className="nav-text">Appointment Management</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span className="nav-text">Logout</span>
          </button>
        </div>
      </div>

      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button
              ref={menuToggleRef}
              className="menu-toggle"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              {isSidebarOpen ? '✕' : '☰'}
            </button>
            <h1>Healthcare Management System</h1>
          </div>
          <div className="header-right">
            <span className="welcome-text">Dr. {authState.doctor?.lastName}</span>
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        <div className="content-area">{renderContent()}</div>
      </div>
    </div>
  );
};

export default Dashboard;
